import { Alert } from "@/components/ui/Alert";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/Collapsible";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/Command";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/HoverCard";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { RadioCard, RadioCardGroup } from "@/components/ui/RadioCard";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Text } from "@/components/ui/Text";
import { safeExternalHttpUrl } from "@/lib/safe-external-url";
import { cn } from "@/lib/utils";
import {
  ArrowUpRight,
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  Info,
  KeyRound,
  Loader2,
  Settings,
  X,
} from "lucide-react";
import type * as React from "react";
import { useId, useState } from "react";
import { Link } from "react-router";
import { ScopeMultiSelect } from "./ScopeMultiSelect";
import type {
  ClientOption,
  ProviderGroup,
  ProviderOption,
  RegistrationChoice,
  RegistrationMethod,
  UserIdentityDraft,
} from "../drafts/useIdentityDraft";

/** What the provider control says before it knows. */
function providerLabel(
  draft: UserIdentityDraft,
  selected: ProviderOption | null,
): string {
  if (selected) return selected.name;
  if (draft.providerLoading) return "Checking\u2026";
  return "Choose an authorization server";
}

/**
 * The frameless combobox trigger the provider and registration pickers share.
 * AIM-230 asks for the provider row to read as settled configuration rather
 * than an unanswered form field, so the control carries no border until it is
 * hovered.
 */
function ScopeTrigger({
  children,
  className,
  ariaLabel,
  // Rest and ref both matter: this renders under <PopoverTrigger asChild>,
  // which clones it with the onClick, aria-expanded and ref that make the
  // menu open. Swallowing them leaves a button that renders and does nothing.
  ...props
}: React.ComponentProps<"button"> & { ariaLabel: string }): JSX.Element {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      {...props}
      className={cn(
        "text-muted-foreground hover:text-foreground hover:bg-muted -mx-1 inline-flex items-center gap-1 px-1 py-0.5 whitespace-nowrap disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
    >
      {children}
    </button>
  );
}

function ProviderItem({
  option,
  selected,
  onSelect,
}: {
  option: ProviderOption;
  selected: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <CommandItem
      value={`${option.name} ${option.url}`}
      onSelect={onSelect}
      className="flex items-center justify-between gap-2"
    >
      <span className="flex min-w-0 flex-col">
        <span className="flex items-center gap-1.5">
          {option.name}
          {option.isNew ? (
            <span className="bg-warning-500 size-1.5 shrink-0 rounded-full" />
          ) : null}
        </span>
        <span className="text-muted-foreground font-mono text-xs">
          {option.url}
        </span>
      </span>
      {selected ? <Check className="size-4 shrink-0" /> : null}
    </CommandItem>
  );
}

/** What a tier says when it has no options to show. */
function emptyTierMessage(group: ProviderGroup, searching: boolean): string {
  if (group.isLoading) return "Loading\u2026";
  if (group.isError) return "Failed to load";
  return searching ? "No matches" : "None yet";
}

function ProviderGroupItems({
  group,
  searching,
  selectedId,
  onSelect,
}: {
  group: ProviderGroup;
  searching: boolean;
  selectedId: string | undefined;
  onSelect: (id: string) => void;
}): JSX.Element | null {
  const empty = group.options.length === 0;
  // A search that turns up nothing in a tier drops the tier, so the menu
  // collapses to the tiers that matched (or to the empty state).
  if (empty && searching && !group.isLoading && !group.isError) return null;

  return (
    <CommandGroup heading={group.tier}>
      {!empty && group.isError ? (
        // Rows can still show (the one in use is always offered), but the
        // tier is incomplete, so say so rather than pass it off as the list.
        <Text variant="small" className="text-destructive px-2 py-1.5">
          Failed to load the rest
        </Text>
      ) : null}
      {empty ? (
        <Text muted variant="small" className="px-2 py-1.5">
          {emptyTierMessage(group, searching)}
        </Text>
      ) : (
        group.options.map((option) => (
          <ProviderItem
            key={option.id}
            option={option}
            selected={option.id === selectedId}
            onSelect={() => onSelect(option.id)}
          />
        ))
      )}
      {group.hasMore ? (
        <CommandItem
          value={`__load_more__${group.tier}`}
          disabled={group.loadingMore}
          onSelect={group.loadMore}
          className="text-muted-foreground cursor-pointer text-xs"
        >
          {group.loadingMore
            ? "Loading\u2026"
            : `More ${group.tier.toLowerCase()} authorization servers`}
        </CommandItem>
      ) : null}
    </CommandGroup>
  );
}

/**
 * One Remote Identity Provider, the client the server uses with it, and how
 * to get a new one — the whole User Identity decision on a single settings
 * row. A connected client reads as a status; clearing it opens the choice of
 * an existing client, automatic registration or manual credentials. The
 * issuer and client records behind it stay on the Remote Identity Provider
 * pages.
 */
export function UserIdentityRow({
  draft,
  disabled,
  createHref,
  clientHref,
}: {
  draft: UserIdentityDraft;
  disabled: boolean;
  createHref: string;
  clientHref: (issuerId: string, clientId: string) => string;
}): JSX.Element {
  const [providerOpen, setProviderOpen] = useState(false);
  const issuerInputId = useId();
  const { selected } = draft;
  const connectedHref =
    selected && draft.connectedClient
      ? clientHref(selected.id, draft.connectedClient.id)
      : null;
  const correctIssuerAction =
    selected?.isNew && !draft.issuerEditorOpen ? (
      <Button
        variant="secondary"
        size="sm"
        disabled={disabled || draft.saving}
        onClick={draft.openIssuerEditor}
      >
        <Button.Text>Correct issuer URL</Button.Text>
      </Button>
    ) : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="relative flex min-w-0 items-center gap-3">
          <div className="bg-card flex size-10 shrink-0 items-center justify-center border">
            <KeyRound aria-hidden="true" className="size-4" />
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <Popover
                open={providerOpen}
                onOpenChange={(open) => {
                  setProviderOpen(open);
                  if (!open) draft.setProviderSearch("");
                }}
              >
                <PopoverTrigger asChild>
                  <ScopeTrigger
                    // Held while discovery is still running: this control is
                    // about to answer its own question, and offering "choose
                    // one" in the meantime invites a pick that the arriving
                    // default would appear to overwrite.
                    disabled={disabled || draft.saving || draft.providerLoading}
                    ariaLabel="Authorization server"
                    className={cn(
                      "max-w-full gap-1.5 text-left break-all whitespace-normal",
                      selected && "text-foreground text-base font-medium",
                    )}
                  >
                    {providerLabel(draft, selected)}
                    <ChevronDown
                      aria-hidden="true"
                      className="text-muted-foreground size-3.5"
                    />
                  </ScopeTrigger>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-80 p-0">
                  {/* Searched on the server, so cmdk must not filter again. */}
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search authorization servers…"
                      value={draft.providerSearch}
                      onValueChange={draft.setProviderSearch}
                      className="h-9"
                    />
                    <CommandList>
                      <CommandEmpty>
                        No authorization servers match.
                      </CommandEmpty>
                      {draft.providerGroups.map((group) => (
                        <ProviderGroupItems
                          key={group.tier}
                          group={group}
                          searching={draft.providerSearch.trim() !== ""}
                          selectedId={selected?.id}
                          onSelect={(id) => {
                            draft.selectProvider(id);
                            setProviderOpen(false);
                            draft.setProviderSearch("");
                          }}
                        />
                      ))}
                    </CommandList>
                  </Command>
                  <div className="border-t p-2">
                    <Link
                      to={createHref}
                      className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm underline underline-offset-2"
                    >
                      Create a custom authorization server
                      <ArrowUpRight aria-hidden="true" className="size-3.5" />
                    </Link>
                  </div>
                </PopoverContent>
              </Popover>
              {selected?.isNew ? (
                <Badge
                  variant="warning"
                  size="sm"
                  title="No matching authorization server exists yet. One is created from what the upstream publishes when you save."
                >
                  <Badge.Text>Will be created</Badge.Text>
                </Badge>
              ) : null}
              <Text
                muted
                variant="small"
                className="font-mono text-xs break-all"
              >
                {selected?.url ?? ""}
              </Text>
            </div>
            {selected ? (
              <ClientStatus draft={draft} disabled={disabled} />
            ) : null}
          </div>
        </div>

        {draft.connected ? (
          <ConnectedSummary draft={draft} advancedHref={connectedHref} />
        ) : null}
        {draft.cleared ? (
          <Button
            variant="tertiary"
            size="sm"
            disabled={disabled}
            onClick={draft.cancelClear}
          >
            <Button.Text>Cancel</Button.Text>
          </Button>
        ) : null}
      </div>

      {draft.providerLoadFailed ? (
        <Alert variant="error" dismissible={false}>
          Couldn&apos;t load this server&apos;s authorization servers. Refresh
          the page to try again.
        </Alert>
      ) : null}

      {draft.providerUnreachable ? (
        <Alert variant="warning" dismissible={false}>
          Couldn&apos;t reach the upstream&apos;s authorization server. Check
          the server URL, or choose an authorization server from the list.
        </Alert>
      ) : null}

      {selected && !draft.connected ? (
        <div className="min-w-0 space-y-4 sm:pl-[52px]">
          {selected.isNew &&
          draft.discoveryStatus === "loading" &&
          !draft.issuerEditorOpen ? (
            <div role="status" className="flex items-center gap-2">
              <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
              <Text muted small>
                Checking OAuth metadata
              </Text>
            </div>
          ) : null}
          {selected.isNew && draft.discoveryStatus === "error" ? (
            <Alert variant="error" dismissible={false} className="min-w-0">
              <code className="block text-xs leading-5 whitespace-pre-wrap [overflow-wrap:anywhere]">
                {draft.discoveryError}
              </code>
            </Alert>
          ) : null}
          {selected.isNew ? (
            draft.issuerEditorOpen || draft.discoveryStatus === "error" ? (
              <div
                id={`${issuerInputId}-editor`}
                role="group"
                aria-label="Issuer correction"
                className="bg-muted/30 min-w-0 space-y-3 border p-4"
              >
                {draft.discoveryStatus === "loading" ? (
                  <div role="status" className="flex items-center gap-2">
                    <Loader2
                      aria-hidden="true"
                      className="size-3.5 shrink-0 animate-spin"
                    />
                    <Text muted small>
                      Checking OAuth metadata
                    </Text>
                  </div>
                ) : draft.issuerEditorValidated ? (
                  <div role="status" className="flex items-center gap-2">
                    <Check
                      aria-hidden="true"
                      className="text-default-success size-4 shrink-0"
                    />
                    <Text small>
                      OAuth metadata discovered. Save to finish setup.
                    </Text>
                  </div>
                ) : null}
                <Label htmlFor={issuerInputId}>Issuer URL</Label>
                <Input
                  id={issuerInputId}
                  value={draft.issuerEditorText}
                  onChange={draft.setIssuerEditorText}
                  disabled={disabled || draft.saving}
                  autoFocus
                  aria-describedby={`${issuerInputId}-help${draft.issuerEditorError ? ` ${issuerInputId}-error` : ""}`}
                  aria-invalid={!!draft.issuerEditorError}
                />
                <Text
                  id={`${issuerInputId}-help`}
                  muted
                  small
                  className="block"
                >
                  Discover retries the current URL or checks a corrected URL
                  before you can save the new provider. Saved providers are not
                  changed.
                </Text>
                {draft.issuerEditorError ? (
                  <div id={`${issuerInputId}-error`}>
                    <Alert variant="error" dismissible={false}>
                      {draft.issuerEditorError}
                    </Alert>
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={
                      disabled ||
                      draft.saving ||
                      draft.capabilitiesLoading ||
                      !draft.issuerEditorText.trim()
                    }
                    onClick={draft.discoverIssuer}
                  >
                    <Button.Text>
                      {draft.capabilitiesLoading
                        ? "Checking…"
                        : draft.issuerEditorValidated
                          ? "Check again"
                          : "Discover"}
                    </Button.Text>
                  </Button>
                  <Button
                    variant="tertiary"
                    size="sm"
                    disabled={disabled || draft.saving}
                    onClick={draft.resetIssuer}
                  >
                    <Button.Text>Reset to advertised URL</Button.Text>
                  </Button>
                </div>
              </div>
            ) : (
              correctIssuerAction
            )
          ) : null}
          <RegistrationChoices draft={draft} disabled={disabled} />
          <ChoiceDetails
            draft={draft}
            disabled={disabled}
            providerName={selected.name}
          />
        </div>
      ) : null}

      {draft.status.kind === "pending" ? (
        <div className="flex items-center gap-2">
          <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
          <Text muted small>
            Registering with {selected?.name ?? "the authorization server"}…
          </Text>
        </div>
      ) : null}
    </div>
  );
}

const STATUS_DOT = "size-2 shrink-0 rounded-full";

/**
 * The line under the provider: whether the server has a client, and the way
 * to clear it. Clearing only changes the draft; Save is what replaces it.
 */
function ClientStatus({
  draft,
  disabled,
}: {
  draft: UserIdentityDraft;
  disabled: boolean;
}): JSX.Element {
  if (draft.connected) {
    return (
      <span className="flex items-center gap-1.5">
        <span
          aria-hidden="true"
          className={cn(STATUS_DOT, "bg-[var(--fill-success-default)]")}
        />
        <Text small>Connected</Text>
        <Button
          variant="tertiary"
          size="xs"
          aria-label="Clear connection"
          tooltip="Clear connection"
          disabled={disabled}
          onClick={draft.clear}
          className="w-7 px-0"
        >
          <Button.LeftIcon>
            <X aria-hidden="true" />
          </Button.LeftIcon>
        </Button>
      </span>
    );
  }
  if (draft.cleared) {
    return (
      <span className="flex items-center gap-1.5">
        <span aria-hidden="true" className={cn(STATUS_DOT, "bg-warning-500")} />
        <Text small warning>
          Unconfigured client
        </Text>
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5">
      <span
        aria-hidden="true"
        className={cn(STATUS_DOT, "border-muted-foreground border")}
      />
      <Text small muted>
        Not connected
      </Text>
    </span>
  );
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** Who is using the connected client, and the way into its settings. */
function ConnectedSummary({
  draft,
  advancedHref,
}: {
  draft: UserIdentityDraft;
  advancedHref: string | null;
}): JSX.Element {
  const scopes = draft.connectedClient?.scopes ?? [];
  return (
    <div className="flex items-center gap-4">
      <Text small muted>
        {draft.signedIn !== null ? (
          <>
            <span className="text-foreground">
              {plural(draft.signedIn, "person", "people")}
            </span>{" "}
            signed in
          </>
        ) : null}
        {draft.signedIn !== null && scopes.length > 0 ? (
          <span className="mx-1.5">·</span>
        ) : null}
        {scopes.length > 0 ? <ScopeList scopes={scopes} /> : null}
      </Text>
      {advancedHref ? (
        <Button variant="tertiary" size="sm" asChild>
          <Link to={advancedHref}>
            <Button.LeftIcon>
              <Settings aria-hidden="true" />
            </Button.LeftIcon>
            <Button.Text>Advanced</Button.Text>
          </Link>
        </Button>
      ) : null}
    </div>
  );
}

/**
 * The connected client's scopes: a count that opens the list. The count is
 * the summary; which scopes were requested is the detail an operator checks
 * when a tool call comes back forbidden.
 */
function ScopeList({ scopes }: { scopes: string[] }): JSX.Element {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="hover:text-foreground underline decoration-dotted underline-offset-3"
        >
          <span className="text-foreground">{scopes.length}</span>{" "}
          {scopes.length === 1 ? "scope" : "scopes"}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-3">
        <Text small muted className="text-eyebrow mb-2 block">
          Requested scopes
        </Text>
        <ul className="space-y-1">
          {scopes.map((scope) => (
            <li key={scope} className="font-mono text-xs break-all">
              {scope}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/** Why a card is unavailable, behind an info mark on its title. */
function UnavailableReason({ reason }: { reason: string }): JSX.Element {
  return (
    <HoverCard openDelay={150}>
      <HoverCardTrigger asChild>
        <span className="inline-flex cursor-help items-center gap-1">
          Not available
          <Info aria-label={reason} className="size-3" />
        </span>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-72">
        <Text small className="block">
          {reason}
        </Text>
      </HoverCardContent>
    </HoverCard>
  );
}

/** The three ways to give a server a client, as one radio group. */
function RegistrationChoices({
  draft,
  disabled,
}: {
  draft: UserIdentityDraft;
  disabled: boolean;
}): JSX.Element {
  const name = draft.selected?.name ?? "the authorization server";
  const loading = draft.clientsLoading || draft.capabilitiesLoading;
  const automaticReason =
    draft.selected?.isNew && draft.discoveryStatus !== "ready"
      ? draft.discoveryStatus === "loading"
        ? "Checking automatic registration."
        : draft.discoveryStatus === "error"
          ? "Could not check automatic registration. Retry OAuth metadata discovery."
          : "Automatic registration has not yet been checked."
      : `${name} has no usable automatic registration method. Automatic registration requires compatible OAuth endpoints and client authentication, using a Client ID Metadata Document or dynamic client registration.`;
  return (
    <RadioCardGroup
      orientation="horizontal"
      value={draft.choice}
      disabled={disabled || loading}
      onValueChange={(value) => draft.selectChoice(value as RegistrationChoice)}
      className="grid-flow-row grid-cols-1 md:grid-flow-col md:grid-cols-none"
    >
      <RadioCard
        value="existing"
        title="Existing client"
        disabled={!draft.existingAvailable}
      >
        {draft.existingAvailable ? (
          `Reuse a client already registered with ${name}.`
        ) : (
          <UnavailableReason
            reason={`No clients are registered with ${name} yet. Auto-Configure or Manual creates the first one.`}
          />
        )}
      </RadioCard>
      <RadioCard
        value="auto"
        title="Auto-Configure"
        disabled={!draft.automaticAvailable}
      >
        {draft.automaticAvailable ? (
          `Speakeasy registers a new client with ${name} when you save.`
        ) : (
          <UnavailableReason reason={automaticReason} />
        )}
      </RadioCard>
      <RadioCard value="manual" title="Manual">
        {`Paste a client ID and secret issued by ${name}.`}
      </RadioCard>
    </RadioCardGroup>
  );
}

/** The fields the selected choice needs, under the cards. */
function ChoiceDetails({
  draft,
  disabled,
  providerName,
}: {
  draft: UserIdentityDraft;
  disabled: boolean;
  providerName: string;
}): JSX.Element | null {
  switch (draft.choice) {
    case "existing":
      return <ExistingClientField draft={draft} disabled={disabled} />;
    case "auto":
      return draft.methodChoiceAvailable ? (
        <AdvancedOptions>
          <RegistrationMethodField draft={draft} disabled={disabled} />
        </AdvancedOptions>
      ) : null;
    case "manual":
      return (
        <ManualCredentialsFields
          draft={draft}
          disabled={disabled}
          providerName={providerName}
        />
      );
    case null:
      return null;
  }
}

function ExistingClientField({
  draft,
  disabled,
}: {
  draft: UserIdentityDraft;
  disabled: boolean;
}): JSX.Element {
  return (
    <div className="max-w-md space-y-1.5">
      <Text small className="block font-medium">
        Client
      </Text>
      <Select
        value={draft.existingClientId ?? undefined}
        onValueChange={draft.selectExisting}
        disabled={disabled}
      >
        <SelectTrigger aria-label="Client">
          <SelectValue placeholder="Choose a client" />
        </SelectTrigger>
        <SelectContent>
          {draft.existingOptions.map((option: ClientOption) => (
            <SelectItem key={option.id} value={option.id}>
              {option.name}
              {option.hint ? (
                <span className="text-muted-foreground ml-2 font-mono text-xs">
                  {option.hint}
                </span>
              ) : null}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {draft.sameAsConnected ? (
        <Text muted small className="block">
          This is the client the server already uses. Choose another to replace
          it.
        </Text>
      ) : null}
    </div>
  );
}

/** A collapsed disclosure for settings most people leave alone. */
function AdvancedOptions({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Collapsible>
      <CollapsibleTrigger className="group text-foreground flex items-center gap-1.5 text-sm">
        <ChevronRight
          aria-hidden="true"
          className="size-3.5 transition-transform group-data-[state=open]:rotate-90"
        />
        Advanced
      </CollapsibleTrigger>
      <CollapsibleContent className="max-w-md space-y-1.5 pt-3 pl-5">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

const REGISTRATION_METHOD_OPTIONS: {
  value: RegistrationMethod;
  label: string;
}[] = [
  { value: "cimd", label: "CIMD" },
  { value: "dcr", label: "DCR" },
];

function RegistrationMethodField({
  draft,
  disabled,
}: {
  draft: UserIdentityDraft;
  disabled: boolean;
}): JSX.Element {
  return (
    <>
      <Text small className="block font-medium">
        Registration method
      </Text>
      <SegmentedControl
        value={draft.registrationMethod}
        onChange={draft.setRegistrationMethod}
        options={REGISTRATION_METHOD_OPTIONS}
        disabled={disabled}
      />
      <Text muted small className="block">
        CIMD publishes the client from Speakeasy; DCR registers it with the
        authorization server.
      </Text>
    </>
  );
}

/** The scopes a manual client requests: advertised ones to pick, or typed. */
function ScopeField({
  draft,
  disabled,
  providerName,
}: {
  draft: UserIdentityDraft;
  disabled: boolean;
  providerName: string;
}): JSX.Element {
  const id = useId();
  const labelId = `${id}-label`;
  return (
    <>
      <Label id={labelId} htmlFor={id} className="block leading-normal">
        Scope
      </Label>
      <ScopeMultiSelect
        id={id}
        labelId={labelId}
        options={draft.scopeOptions}
        value={draft.scopes}
        onValueChange={draft.setScopes}
        placeholder="Default scopes"
        disabled={disabled}
      />
      <Text muted small className="block">
        Choose from the scopes this server and {providerName} advertise, or type
        one to add it. Leave blank to request the default scopes.
      </Text>
    </>
  );
}

function ManualCredentialsFields({
  draft,
  disabled,
  providerName,
}: {
  draft: UserIdentityDraft;
  disabled: boolean;
  providerName: string;
}): JSX.Element {
  // The guide URL comes from issuer metadata, so it is upstream input: only
  // render the action once it is known to be an ordinary http(s) link.
  const registrationGuideUrl = safeExternalHttpUrl(draft.registrationGuideUrl);
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Text muted small className="block">
            Client ID
          </Text>
          <Input
            value={draft.clientId}
            onChange={draft.setClientId}
            placeholder={`from ${providerName}`}
            disabled={disabled}
            aria-label="Client ID"
            noAutofill
          />
        </div>
        <div className="space-y-1">
          <Text muted small className="block">
            Client secret
          </Text>
          <Input
            type="password"
            value={draft.clientSecret}
            onChange={draft.setClientSecret}
            placeholder="Optional"
            disabled={disabled}
            aria-label="Client secret"
            noAutofill
          />
        </div>
      </div>
      <AdvancedOptions>
        <ScopeField
          draft={draft}
          disabled={disabled}
          providerName={providerName}
        />
      </AdvancedOptions>
      {registrationGuideUrl ? (
        <Button variant="secondary" size="sm" asChild>
          <a href={registrationGuideUrl} target="_blank" rel="noreferrer">
            <Button.LeftIcon>
              <BookOpen aria-hidden="true" className="size-3.5" />
            </Button.LeftIcon>
            <Button.Text>Open registration guide</Button.Text>
          </a>
        </Button>
      ) : null}
    </div>
  );
}
